/**
 * W-FAQ-Product Component
 * Handles FAQ category switching and accordion behavior
 */

class FaqProduct extends HTMLElement {
  constructor() {
    super();
    this.categoryButtons = this.querySelectorAll('.w-faq-product__category');
    this.categoryGroups = this.querySelectorAll('.w-faq-product__category-group');
    this.init();
  }

  init() {
    this.setupCategoryListeners();
    this.setupAccordionBehavior();
  }

  /**
   * Setup category tab switching
   */
  setupCategoryListeners() {
    this.categoryButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const categoryHandle = button.getAttribute('data-category');
        this.switchCategory(categoryHandle, button);
      });
    });
  }

  /**
   * Switch to the selected category
   * @param {string} categoryHandle - The category handle to display
   * @param {HTMLElement} clickedButton - The button that was clicked
   */
  switchCategory(categoryHandle, clickedButton) {
    // Update active state on buttons
    this.categoryButtons.forEach((btn) => {
      btn.classList.remove('active');
    });
    clickedButton.classList.add('active');

    // Update visible category group
    this.categoryGroups.forEach((group) => {
      const groupCategory = group.getAttribute('data-category-group');
      if (groupCategory === categoryHandle) {
        group.classList.add('active');
        // Close all open details in the newly displayed group
        this.closeAllDetails(group);
      } else {
        group.classList.remove('active');
      }
    });

    // Scroll category into view on mobile if needed
    if (window.innerWidth < 989) {
      clickedButton.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }

  /**
   * Setup accordion behavior for FAQ items
   */
  setupAccordionBehavior() {
    const detailsElements = this.querySelectorAll('.w-faq-product__item');

    detailsElements.forEach((details) => {
      const summary = details.querySelector('.w-faq-product__question');

      if (summary) {
        // Prevent default details behavior to add custom animation
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleDetails(details);
        });
      }
    });
  }

  /**
   * Toggle a details element with smooth animation
   * @param {HTMLDetailsElement} details - The details element to toggle
   */
  toggleDetails(details) {
    const answer = details.querySelector('.w-faq-product__answer');

    if (details.open) {
      // Closing
      this.closeDetails(details, answer);
    } else {
      // Opening - close other open details in the same group first
      const group = details.closest('.w-faq-product__category-group');
      this.closeAllDetails(group);
      this.openDetails(details, answer);
    }
  }

  /**
   * Open a details element with animation
   * @param {HTMLDetailsElement} details - The details element
   * @param {HTMLElement} answer - The answer content element
   */
  openDetails(details, answer) {
    details.open = true;

    if (answer && answer.slideDown) {
      answer.slideDown(300);
    }
  }

  /**
   * Close a details element with animation
   * @param {HTMLDetailsElement} details - The details element
   * @param {HTMLElement} answer - The answer content element
   */
  closeDetails(details, answer) {
    if (answer && answer.slideUp) {
      answer.slideUp(300, () => {
        details.open = false;
      });
    } else {
      details.open = false;
    }
  }

  /**
   * Close all open details in a group
   * @param {HTMLElement} group - The category group element
   */
  closeAllDetails(group) {
    if (!group) return;

    const openDetails = group.querySelectorAll('.w-faq-product__item[open]');
    openDetails.forEach((details) => {
      const answer = details.querySelector('.w-faq-product__answer');
      this.closeDetails(details, answer);
    });
  }
}

// Register the custom element
if (!customElements.get('faq-product')) {
  customElements.define('faq-product', FaqProduct);
}

// Initialize for sections loaded after page load (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const faqProduct = section.querySelector('faq-product');

  if (faqProduct && !customElements.get('faq-product')) {
    customElements.define('faq-product', FaqProduct);
  }
});
